import React, { useState, useEffect } from 'react';
import Logo from '../../assets/images/logo.svg';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  Container,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  DialogContentText,
  DialogTitle,
  Checkbox,
  IconButton,
} from '@mui/material';
import { SignupAssetRegisterInitialFormData } from '../../types/signup';
import { CustomColors, theme } from '../../theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import axios from 'axios';
import { toast } from 'react-toastify';
import PlatformAgreement from '../../components/PlatformAgreement';
import Dropzone, { FileRejection } from 'react-dropzone';
import {
  dropzoneStyle,
  imageContainerStyle,
  imageStyle,
  removeButtonStyle,
  fileIconContainerStyle,
  closeButtonContainerStyle,
} from './styles';
import CloseIcon from '@mui/icons-material/Close';
import { FileOpen } from '@mui/icons-material';
import AttachmentIcon from '../../assets/images/attachments.svg';
import DeleteIcon from '@mui/icons-material/Delete';

const AssetRegister: React.FC = () => {
  const isSmallScreen = useMediaQuery(
    '(max-width: 900px) and (min-width: 520px)'
  );
  const isMobile = useMediaQuery('(max-width: 520px)');
  const [showLoader, setShowLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const descriptionElementRef = React.useRef<HTMLElement>(null);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);

  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm<SignupAssetRegisterInitialFormData>();

  const [fileError, setFileError] = useState('');
  const [forceUpdate, setForceUpdate] = useState<number>(0);
  const uploadedImages: string[] = getValues('attachments') || [];

  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [uploadedImages]);

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    setFileError('');
    const allowedMimeTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/msword',
      'text/doc',
      'text/docx',
      'text/pdf',
      'text/xls',
      'text/xlsx',
    ];
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    const newFileNames: string[] = []; // Array to hold file names

    acceptedFiles.forEach((file) => {
      if (allowedMimeTypes.includes(file.type)) {
        validFiles.push(file);
        newFileNames.push(file.name);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const invalidFilesInfo = invalidFiles.map((file) => ({
        file: file,
        errors: ['Invalid file type. Only DOCS, PDF, Excel are allowed.'],
      }));
      console.log('Invalid files:', invalidFilesInfo);
      setFileError('Invalid file type. Only DOCS, PDF, Excel are allowed.');
    }

    if (rejectedFiles.length > 0) {
      const rejectedFilesInfo = rejectedFiles.map((rejectedFile) => ({
        file: rejectedFile.file,
        errors: rejectedFile.errors,
      }));
      console.log(rejectedFilesInfo);
      setFileError('File Size must be less than 10MB.');
      return;
    }

    const newImages = await Promise.all(
      validFiles.map(async (file) => {
        const base64Image = await readFileAsBase64(file);
        return base64Image;
      })
    );

    setUploadedFileNames((prevNames) => [...prevNames, ...newFileNames]); // Update file names
    setValue('attachments', [...newImages]);
    setForceUpdate((prev) => prev + 1);
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const newImages = uploadedImages.filter(
      (_, index) => index !== indexToRemove
    );
    setValue('attachments', newImages);
    setForceUpdate((prev) => prev + 1);
  };

  const onSubmit: SubmitHandler<SignupAssetRegisterInitialFormData> = async (
    data
  ) => {
    setLoading(true);

    if (Object.keys(errors).length === 0) {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/registerasset/signup`,
        {
          ...data,
        }
      );

      if (response.status === 201) {
        setShowLoader(true);
        setLoading(false);

        setTimeout(() => {
          navigate('/registerasset-success');
        }, 3000);
      } else {
        setLoading(false);
        toast.error('Something went wrong');
      }
    }
    setLoading(false);
    return;
  };

  const styles: { [key: string]: React.CSSProperties } = {
    h1: {
      fontSize: theme.spacing(5),
      lineHeight: theme.spacing(5),
      fontWeight: 500,
      marginLeft: isSmallScreen ? 0 : isMobile ? 0 : '100px',
      width: isSmallScreen ? '50%' : '100%',
      color: CustomColors.foregroundColor,
    },
    p: {
      width: isSmallScreen ? '50%' : '100%',
      marginTop: 3,
      marginLeft: isSmallScreen ? 0 : isMobile ? 0 : '100px',
      color: CustomColors.grey,
    },

    container: {
      marginTop: theme.spacing(8),
      padding: theme.spacing(5),
    },
    mainContainer: {
      padding: isSmallScreen ? '50px' : 0,
    },
    signUpContainer: {
      marginTop: isSmallScreen ? 0 : isMobile ? 0 : theme.spacing(8),
      paddingRight: isSmallScreen ? 0 : isMobile ? 0 : '150px',
    },
    logo: {
      width: theme.spacing(20),
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
    button: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
  };

  return (
    <div>
      <Container maxWidth="xl">
        <img src={Logo} alt="BlueSana" style={styles.logo} />

        <Grid container spacing={2} sx={styles.mainContainer}>
          <Grid item xs={12} md={6} sx={styles.container}>
            <Grid container>
              <Grid item xs={12} md={2}></Grid>
              <Grid item xs={12} md={6} sx={styles.text}>
                <Typography variant="h1" sx={styles.h1}>
                  TELL US ABOUT YOUR BUSINESS
                </Typography>

                <Typography sx={styles.p}>
                  All businesses that feature on the BlueSana platform go
                  through a review process. Please fill out the form and attach
                  your investor deck if you have one.
                  <br />
                  In the comments box please provide details around about your
                  raise. We will be in contact when we have reviewed your
                  response.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} sx={styles.signUpContainer}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      name="firstName"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: 'First Name is required',
                        minLength: {
                          value: 2,
                          message: 'First Name must be at least 2 characters',
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label="First Name"
                            variant="outlined"
                            error={!!errors.firstName}
                          />
                          {errors.firstName && (
                            <FormHelperText error>
                              {errors.firstName.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      name="lastName"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: 'Last Name is required',
                        minLength: {
                          value: 2,
                          message: 'Last Name must be at least 2 characters',
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            label="Last Name"
                            variant="outlined"
                            error={!!errors.lastName}
                          />
                          {errors.lastName && (
                            <FormHelperText error>
                              {errors.lastName.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Email"
                        variant="outlined"
                        error={!!errors.email}
                      />
                      {errors.email && (
                        <FormHelperText error>
                          {errors.email.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Phone is required',
                    pattern: {
                      value: /^(\+\d{1,3})?\d{8,14}$/,
                      message: 'Invalid phone number',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Phone"
                        variant="outlined"
                        error={!!errors.phone}
                      />
                      {errors.phone && (
                        <FormHelperText error>
                          {errors.phone.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="entityName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Name"
                        variant="outlined"
                      />
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="location"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Location"
                        variant="outlined"
                      />
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="entityWebAddress"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^(www\.)?[^ "]+$/,
                      message: 'Invalid URL format',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Company Web Address"
                        variant="outlined"
                      />
                      {errors.entityWebAddress && (
                        <FormHelperText error>
                          {errors.entityWebAddress.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="linkedInProfile"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?linkedin.com\/[^ "]+$/,
                      message: 'Invalid LinkedIn Profile URL format',
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="LinkedIn Profile"
                        variant="outlined"
                      />
                      {errors.linkedInProfile && (
                        <FormHelperText error>
                          {errors.linkedInProfile.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Controller
                  name="comment"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Comment is required',
                  }}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="Comment"
                        rows={4}
                        multiline
                        variant="outlined"
                        error={!!errors.comment}
                      />
                      <FormHelperText
                        style={{ color: CustomColors.foregroundColor }}
                      >
                        Please add any other relevant information and background
                        on why you want access to the BlueSana platform.
                      </FormHelperText>
                      {errors.comment && (
                        <FormHelperText error>
                          {errors.comment.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>

              <Grid item xs={12} md={12}>
                <Dropzone
                  onDrop={onDrop}
                  multiple={true}
                  maxSize={10 * 1024 * 1024}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} style={dropzoneStyle}>
                      <input {...getInputProps()} />
                      <p>Please attach Investor Deck.</p>
                    </div>
                  )}
                </Dropzone>
                {fileError !== '' && (
                  <Typography color={'error'}> {fileError}</Typography>
                )}
              </Grid>

              <Grid item xs={12} md={12} style={{ marginTop: 20 }}>
                {uploadedImages.map((image, index) => (
                  <Grid
                    key={index}
                    container
                    justifyContent={'space-between'}
                    spacing={2}
                    sx={{
                      paddingX: '20px',
                      borderBottom: '1px solid #ccc',
                    }}
                  >
                    <Grid item>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          padding: '10px',
                        }}
                      >
                        <img src={AttachmentIcon} alt="icon" />
                        <Typography
                          variant="body1"
                          style={{ marginLeft: '10px' }}
                        >
                          {uploadedFileNames[index].slice(0, 20)}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => handleRemoveImage(index)}
                        size="small"
                      >
                        <DeleteIcon style={{ color: '#000' }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Button
                style={styles.button}
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                endIcon={<ChevronRightIcon />}
              >
                Signup
              </Button>
            </form>
          </Grid>
        </Grid>
      </Container>

      {showLoader && <Loading heading="Loading" text="Please Wait...." />}
    </div>
  );
};

export default AssetRegister;
